export function parseExifIFDs(
    view: DataView,
    exifBase: number,
    littleEndian: boolean,
    firstIFDOffset: number,
    onCoords: (lat: number | null, lng: number | null) => void): void {

    const readRational = (offset: number): [number, number] => [
        safeU32(view, offset, littleEndian) ?? 0,
        safeU32(view, offset + 4, littleEndian) ?? 1,
    ];

    const toDecimal = (parts: [number, number][]): number => {
        const d = parts[0][0] / parts[0][1];
        const m = parts[1][0] / parts[1][1];
        const s = parts[2][0] / parts[2][1];
        return d + m / 60 + s / 3600;
    };

    let lat: number | null = null;
    let lng: number | null = null;

    const readAsciiRef = (entry: number): string | null => {
        const valueOffset = entry + 8;
        if (valueOffset + 4 > view.byteLength) return null;

        // Try reading as inline string (null-terminated, within 4 bytes)
        let str = '';
        for (let i = 0; i < 4; i++) {
            const byte = view.getUint8(valueOffset + i);
            if (byte === 0) break;
            str += String.fromCharCode(byte);
        }
        return str || null;
    };

    const parseIFD = (ifdStart: number): void => {
        const count = safeU16(view, ifdStart, littleEndian);
        if (count === null) return;

        let latRef: 'N' | 'S' | null = null;
        let lngRef: 'E' | 'W' | null = null;
        let rawLat: number | null = null;
        let rawLng: number | null = null;

        for (let i = 0; i < count; i++) {
            const entry = ifdStart + 2 + i * 12;

            const tag = safeU16(view, entry, littleEndian);
            const format = safeU16(view, entry + 2, littleEndian);
            const components = safeU32(view, entry + 4, littleEndian);
            if (tag === null || format === null || components === null) continue;

            // GPS IFD pointer: 0x8825
            if (tag === 0x8825 && format === 4 && components === 1) {
                const ptr = safeU32(view, entry + 8, littleEndian);
                if (ptr !== null && exifBase + ptr + 2 < view.byteLength) {
                    parseIFD(exifBase + ptr);
                }
                continue;
            }

            // GPSLatitudeRef: 0x0001
            if (tag === 0x0001 && format === 2 && components === 2) {
                const ptr = safeU32(view, entry + 8, littleEndian);
                if (ptr !== null) {
                    const ref = safeStr(view, exifBase + ptr, 2);
                    if (ref === 'N') latRef = 'N';
                    else if (ref === 'S') latRef = 'S';
                }
            }

            // GPSLongitudeRef: 0x0003
            if (tag === 0x0003 && format === 2 && components === 2) {
                const ref = readAsciiRef(entry);
                if (ref === 'E') lngRef = 'E';
                else if (ref === 'W') lngRef = 'W';
            }

            // GPSLatitude: 0x0002
            if (tag === 0x0002 && format === 5 && components === 3) {
                const ptr = safeU32(view, entry + 8, littleEndian);
                if (ptr !== null) {
                    const dataOff = exifBase + ptr;
                    const parts: [number, number][] = [];
                    for (let j = 0; j < 3; j++) parts.push(readRational(dataOff + j * 8));
                    rawLat = toDecimal(parts);
                }
            }

            // GPSLongitude: 0x0004
            if (tag === 0x0004 && format === 5 && components === 3) {
                const ptr = safeU32(view, entry + 8, littleEndian);
                if (ptr !== null) {
                    const dataOff = exifBase + ptr;
                    const parts: [number, number][] = [];
                    for (let j = 0; j < 3; j++) parts.push(readRational(dataOff + j * 8));
                    rawLng = toDecimal(parts);
                }
            }
        }

        // Apply signs AFTER all tags are parsed
        if (rawLat != null && !Number.isNaN(rawLat)) {
            lat = latRef === 'S' ? -rawLat : rawLat;
        }
        if (rawLng != null && !Number.isNaN(rawLng)) {
            lng = lngRef === 'W' ? -rawLng : rawLng;
        }

        // Next IFD
        const next = safeU32(view, ifdStart + 2 + count * 12, littleEndian);
        if (next && next !== 0 && exifBase + next + 2 < view.byteLength) {
            parseIFD(exifBase + next);
        }
    };

    const ifd0 = exifBase + firstIFDOffset;
    if (ifd0 + 2 < view.byteLength) {
        parseIFD(ifd0);
    }
    onCoords(lat, lng);
}

// Helper functions used by parseExifIFDs
export const safeU16 = (view: DataView, offset: number, littleEndian: boolean): number | null =>
    offset + 2 <= view.byteLength ? view.getUint16(offset, littleEndian) : null;

export const safeU32 = (view: DataView, offset: number, littleEndian: boolean): number | null =>
    offset + 4 <= view.byteLength ? view.getUint32(offset, littleEndian) : null;

export const safeStr = (view: DataView, offset: number, length: number): string | null => {
    if (offset + length > view.byteLength) return null;
    let str = '';
    for (let i = 0; i < length; i++) {
        const byte = view.getUint8(offset + i);
        if (byte === 0) break;
        str += String.fromCharCode(byte);
    }
    return str;
};

export interface GPSCoords {
    latitude: number | null;
    longitude: number | null;
}

export function extractGPSFromImage(file: File): Promise<GPSCoords> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const buffer = e.target?.result as ArrayBuffer;
            if (!buffer || buffer.byteLength < 20) {
                return reject(new Error('File too small'));
            }

            const view = new DataView(buffer);
            let latitude: number | null = null;
            let longitude: number | null = null;

            // ———————— JPEG ————————
            const isJpeg = safeU16(view, 0, false) === 0xffd8;
            if (isJpeg) {
                let offset = 2;
                while (offset + 4 < view.byteLength) {
                    const marker = safeU16(view, offset, false);
                    if (marker === null) break;

                    if (marker !== 0xffe1) {
                        const len = safeU16(view, offset + 2, false);
                        if (len === null) break;
                        offset += 2 + len;
                        continue;
                    }

                    const segLen = safeU16(view, offset + 2, false);
                    if (segLen === null || segLen < 6) {
                        if (segLen !== null) {
                            offset += 2 + segLen;
                        }
                        continue;
                    }

                    if (safeStr(view, offset + 4, 6) !== 'Exif') {
                        offset += 2 + segLen;
                        continue;
                    }

                    const exifBase = offset + 10;
                    const endian = safeU16(view, exifBase, false);
                    if (!endian || (endian !== 0x4949 && endian !== 0x4d4d)) break;
                    const little = endian === 0x4949;

                    const firstIFDOffset = safeU32(view, exifBase + 4, little);
                    if (firstIFDOffset === null) break;
                    
                    parseExifIFDs(view, exifBase, little, firstIFDOffset, (lat: number | null, lng: number | null) => {
                        latitude = lat;
                        longitude = lng;
                    });
                    break;
                }
            }

            // ———————— WebP ————————
            else if (
                safeStr(view, 0, 4) === 'RIFF' &&
                safeStr(view, 8, 4) === 'WEBP'
            ) {
                let offset = 12; // after RIFF + size + WEBP

                while (offset + 8 < view.byteLength) {
                    const chunkId = safeStr(view, offset, 4);
                    const chunkSize = safeU32(view, offset + 4, true); // WebP uses little‑endian
                    if (chunkId === null || chunkSize === null) break;

                    const chunkEnd = offset + 8 + chunkSize + (chunkSize % 2);

                    if (chunkId === 'EXIF') {
                        const exifBase = offset + 8;
                        const endian = safeU16(view, exifBase, false);
                        if (endian && (endian === 0x4949 || endian === 0x4d4d)) {
                            const little = endian === 0x4949;
                            const firstIFDOffset = safeU32(view, exifBase + 4, little);
                            if (firstIFDOffset !== null) {
                                parseExifIFDs(view, exifBase, little, firstIFDOffset, (lat: number | null, lng: number | null) => {
                                    latitude = lat;
                                    longitude = lng;
                                });
                                break;
                            }
                        }
                    }

                    offset = chunkEnd;
                }
            }

            if (latitude === null || longitude === null) {
                return resolve({latitude: null, longitude: null});
            }

            resolve({ latitude, longitude });
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
};